import {Component, type ErrorInfo, type ReactNode} from 'react';
import type {ErrorDetails, Resource} from './lib/types';
import {APP_BASE, Link} from './router';
import {PRODUCT_NAME} from '../src/product';
import {Loading} from './main-loading';
import {usePublicLocale} from './public-locale';
import {errorDiagnostics, errorPresentation} from './lib/error-presentation';
import './error-notice.css';
export {Loading} from './main-loading';

export function shortId(value: string): string {return `${value.slice(0, 6)}…${value.slice(-4)}`;}
export function dateTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) throw new Error(`Invalid recorded date: ${value}`);
  return new Intl.DateTimeFormat(undefined, {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'}).format(date);
}
export function ErrorNotice({error, title}: {error: ErrorDetails | null; title?: string}) {
  if (!error) return null;
  const content = errorPresentation(error, title);
  return <div className="error-notice" role="alert">
    <h3>{content.title}</h3><p>{content.message}</p>
    {content.action && <Link className="error-action" href={`${APP_BASE}${content.action.path}`}>{content.action.label}</Link>}
    <details><summary>Details</summary><pre>{errorDiagnostics(error)}</pre></details>
  </div>;
}
export function ResourceView<T>({resource, children}: {
  resource: Resource<T>; children: (data: T) => ReactNode;
}) {
  if (resource.status === 'error') return <ErrorNotice error={resource.error}/>;
  if (resource.data === null) return <Loading/>;
  return <>{children(resource.data)}</>;
}
export function Empty({title, children, action}: {title: string; children?: ReactNode; action?: ReactNode}) {
  return <div className="empty-state"><h2>{title}</h2>{children && <p>{children}</p>}{action}</div>;
}
export function PageHeading({eyebrow, title, children, action}: {eyebrow?: string; title: string; children?: ReactNode; action?: ReactNode}) {
  return <header className="page-heading"><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}
    <h1>{title}</h1>{children && <p className="page-description">{children}</p>}</div>{action}</header>;
}
export function Brand() {
  const {t} = usePublicLocale();
  return <Link className="brand eightbots-wordmark" href={`${APP_BASE}/`} aria-label={t(`${PRODUCT_NAME} home`)}>EIGHTBOTS</Link>;
}
export class ErrorBoundary extends Component<{children: ReactNode}, {error: ErrorDetails | null}> {
  state = {error: null as ErrorDetails | null};
  static getDerivedStateFromError(error: Error) {return {error: {name: error.name, message: error.message, stack: error.stack}};}
  componentDidCatch(error: Error, detail: ErrorInfo) {console.error(error, detail.componentStack);}
  render() {
    if (this.state.error) return <main className="page"><Brand/><PageHeading title="The page could not be shown"/>
      <ErrorNotice error={this.state.error}/><button type="button" className="button" onClick={() => window.location.reload()}>Reload page</button></main>;
    return this.props.children;
  }
}
